<template>
  <div class="ai-agent-wizard">
    <div>
      Transaction type: <span>{{ structure.type }}</span>
    </div>
    <div>
      Transaction date: <span>{{ structure.date }}</span>
    </div>
    <div v-if="structure.description">
      Transaction description: <span>{{ structure.description }}</span>
    </div>
    <div v-if="structure.Account">
      Account:
      <span> {{ structure.Account.name }} ({{ structure.Account.type }}) </span>
    </div>
    <div>
      Amount: <span>{{ amount }}</span>
    </div>
    <div v-if="structure.Asset">
      Asset:
      <span>{{ structure.Asset.name }} ({{ structure.Asset.type }})</span>
    </div>
    <div v-if="structure.Asset.description">
      Asset description:
      <span>{{ structure.Asset.description }}</span>
    </div>
    <div v-if="structure.Contributions">
      Contributions: <span>{{ structure.Contributions }}</span>
    </div>
    <div v-if="structure.Distributions">
      Distributions: <span>{{ structure.Distributions }}</span>
    </div>
    <div v-if="structure.Values">
      Values:
      <div v-for="value in structure.Values" :key="value.date">
        &dash;
        <span>{{ value.date }}: </span>
        <span>{{ numberFormat(value.close, { currency }) }}</span>
      </div>
    </div>
    <div v-if="structure.Valuation">
      Valuation:
      <div v-for="value in structure.Valuation" :key="value.date">
        &dash;
        <span>{{ value.date }}: </span>
        <span>{{ numberFormat(value.close, { currency }) }}</span>
      </div>
    </div>
    <div>
      AssetClass: <span>{{ structure.AssetClass }}</span>
    </div>
    <div>
      InvestmentStrategy: <span>{{ structure.InvestmentStrategy }}</span>
    </div>
    <UIButton v-if="!disable" size="sm" class="mt-2" @click="handleApply">
      Apply
    </UIButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, markRaw } from 'vue'

import { AssetClass, TransactionClass } from '@types'
import type { AIAgentAnswerStructure } from '@/store/ai/agent/utils/types'

import { ASSET_CLASS, INVESTMENT_STRATEGY } from '@/modules/wizards/utils/const'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'

import { numberFormat } from '@/helpers/numbers'
import { NEGATIVE_RULES } from '@/helpers/validate'

import AddFineArtAndCollectables from '@/modules/wizards/FineArtAndCollectables/FineArtAndCollectables.vue'
import AddLiability from '@/modules/wizards/Liability/Liability.vue'
import AddPrivateFund from '@/modules/wizards/PrivateFund/PrivateFund.vue'
import AddPrivateSecurityOrOther from '@/modules/wizards/PrivateSecurityOrOther/PrivateSecurityOrOther.vue'
import AddRealEstate from '@/modules/wizards/RealEstate/RealEstate.vue'
import { UIButton } from '@ui/buttons'

type Props = {
  name: string
  structure: AIAgentAnswerStructure
  disable: boolean
}

type Emits = {
  apply: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modalsStore = useModalsStore()
const assetsBunchStore = useAssetsBunchStore()

const currency = inject<ComputedRef<string>>('currency')

const amount = computed(() => {
  return numberFormat(Math.abs(props.structure.Amount), {
    currency: currency?.value,
  })
})

const handleApply = () => {
  let component: any
  let account: AssetClass | undefined
  let asset: AssetClass | undefined
  account = assetsBunchStore.getElementByName(props.structure.Account.name)
  if (!account) {
    account = new AssetClass(
      {
        type: props.structure.Account.type,
        name: props.structure.Account.name,
        ticker: props.structure.Account.ticker,
      },
      true,
    )
  }
  asset = new AssetClass({
    type: props.structure.Asset.type,
    name: props.structure.Asset.name,
    ticker: props.structure.Asset.ticker,
    description: props.structure.Asset.description,
    currency: props.structure.Asset.currency,
  })
  const transaction = new TransactionClass({
    date: props.structure.date,
    type: props.structure.type,
    description: props.structure.description,
    entries: [
      { account_id: account.id || '', amount: 1, asset_id: '' },
      {
        account_id: '',
        amount: props.structure.Amount ? Number(props.structure.Amount) : null,
        asset_id: '',
      },
    ],
  }, false, false)
  const amountRule = transaction.settings?.entries[1].amount?.rule
  if (
    amountRule &&
    NEGATIVE_RULES.includes(amountRule) &&
    props.structure.Amount
  ) {
    transaction.entry2().value = {
      ...transaction.entry2().value,
      amount: -Math.abs(props.structure.Amount)
    }
  }
  const prices = props.structure.Values || props.structure.Valuation
  const tags = {
    [ASSET_CLASS]: props.structure.AssetClass,
    [INVESTMENT_STRATEGY]: props.structure.InvestmentStrategy,
  }
  const name = props.name.toLowerCase()
  switch (name) {
    case 'add fine art and collectables':
      component = AddFineArtAndCollectables
      break
    case 'add liability':
      component = AddLiability
      break
    case 'add private fund':
      component = AddPrivateFund
      break
    case 'add private security or other':
      component = AddPrivateSecurityOrOther
      break
    case 'add real estate':
      component = AddRealEstate
      break
  }
  if (!component) return
  const modalInstance = modalsStore.init(`ai-wizard-result-${name.replace(' ', '-')}`, markRaw(component))
  modalInstance?.open(modalsStore.getZIndex(), {
    preset: {
      account,
      asset,
      transaction,
      prices,
      tags,
    },
  })
  emit('apply')
}
</script>

<style>
.ai-agent-wizard {
  @apply flex flex-col gap-0.5;
  @apply border border-common-border rounded-lg p-4;
  @apply text-base sm:text-sm;
  @apply text-gray-500 dark:text-gray-400;

  span {
    @apply text-black dark:text-white;
  }
}
</style>
